import { deploymentPipelineTargetWorkflows } from '@dev-console/deployments';
import type { DeploymentPipelineResponse, DeploymentPipelineTarget } from '@dev-console/types';
import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import { setDeploymentPipelines } from '@/config/api-helpers';

interface DisablePipelinesDialogProps {
  pipeline: DeploymentPipelineTarget;
  /** `disable` = stop deploys; `enable` = resume deploys. */
  action: 'disable' | 'enable';
  onClose: () => void;
  onSuccess: (result: DeploymentPipelineResponse) => void;
  onError: (err: unknown) => void;
}

type DialogState = 'confirm' | 'loading' | 'success';

function pipelineCopy(pipeline: DeploymentPipelineTarget) {
  switch (pipeline) {
    case 'all':
      return {
        subject: 'all deploys',
        workflows: 'staging, production, and OTA update workflows',
        disableBody:
          'This disables the staging and production deploy workflows and the OTA update workflows in GitHub ' +
          'Actions, so no new runs start until they are enabled again. Runs already queued or in progress are not ' +
          'cancelled. Other deploy workflows (scripts, EKS, Pages, and so on) are not touched. Rollback stays available.',
        enableBody:
          'This re-enables the staging, production, and OTA update workflows. Any of them that were disabled on ' +
          'purpose before the incident are re-enabled too.',
        confirmDisable: 'Confirm disable all deploys',
      };
    case 'ota':
      return {
        subject: 'OTA updates',
        workflows: 'OTA update workflows',
        disableBody:
          'This disables the OTA update workflows in GitHub Actions for every environment (staging, staging-legacy, ' +
          'and production), so no new OTA update starts until they are enabled again. Runs already in progress are ' +
          'not cancelled. EAS builds are not affected.',
        enableBody: 'This re-enables the OTA update workflows so OTA updates can be published again.',
        confirmDisable: 'Confirm disable OTAs',
      };
    default:
      return {
        subject: `${pipeline} deploys`,
        workflows: `${pipeline} deploy workflows`,
        disableBody:
          `This disables the ${pipeline} runtime and infrastructure deploy workflows in GitHub Actions (Actions ` +
          'disable-workflow). New deploys will not start until workflows are enabled again.' +
          (pipeline === 'production' ? ' Rollback stays available.' : ''),
        enableBody: `This re-enables the ${pipeline} runtime and infrastructure deploy workflows so deploys can run again.`,
        confirmDisable: pipeline === 'production' ? 'Confirm disable production' : 'Confirm disable',
      };
  }
}

export function DisablePipelinesDialog({ pipeline, action, onClose, onSuccess, onError }: DisablePipelinesDialogProps) {
  const [state, setState] = useState<DialogState>('confirm');
  const [result, setResult] = useState<DeploymentPipelineResponse | null>(null);
  const titleId = useId();

  const copy = pipelineCopy(pipeline);
  const isDisable = action === 'disable';
  // Everything except staging can stop production (OTAs included), so it gets the red confirm.
  const isDangerous = isDisable && pipeline !== 'staging';
  const workflowFiles = deploymentPipelineTargetWorkflows(pipeline);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (state === 'loading') return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, state]);

  const handleConfirm = async () => {
    setState('loading');
    try {
      const response = await setDeploymentPipelines({ pipeline, action });
      setResult(response);
      setState('success');
      onSuccess(response);
    } catch (err) {
      setState('confirm');
      onError(err);
      onClose();
    }
  };

  const title = isDisable ? `Disable ${copy.subject}` : `Enable ${copy.subject}`;

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4'
    >
      <div className='flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 id={titleId} className='text-lg font-medium text-neutral-900'>
            {title}
          </h3>
        </div>

        <div className='min-h-0 space-y-4 overflow-y-auto px-6 py-5'>
          {state === 'confirm' && (
            <>
              <div
                className={`rounded-md border p-3 text-sm ${
                  isDangerous
                    ? 'border-red-300 bg-red-50 text-red-950'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-700'
                }`}
              >
                <p>{isDisable ? copy.disableBody : copy.enableBody}</p>
              </div>

              <div className='text-sm text-neutral-600'>
                <p className='mb-1 font-medium text-neutral-800'>Workflows:</p>
                <ul className='list-inside list-disc space-y-0.5 break-all font-mono text-xs'>
                  {workflowFiles.map((file) => (
                    <li key={file}>{file}</li>
                  ))}
                </ul>
                <p className='mt-3'>
                  This is <span className='font-medium text-neutral-800'>not</span> a{' '}
                  <code className='rounded bg-neutral-100 px-1'>FREEZE_ENABLED</code> /{' '}
                  <code className='rounded bg-neutral-100 px-1'>STAGING_FREEZE_ENABLED</code> freeze — those repo
                  variables and <code className='rounded bg-neutral-100 px-1'>deployment-freeze.yml</code> are a
                  separate path.
                  {pipeline !== 'ota' && (
                    <>
                      {' '}
                      Rollback (<code className='rounded bg-neutral-100 px-1'>rollback-deploy.yml</code>) is not
                      toggled.
                    </>
                  )}{' '}
                  Notifies <code className='rounded bg-neutral-100 px-1'>#eng-deployments</code>.
                </p>
              </div>
            </>
          )}

          {state === 'loading' && (
            <p className='py-6 text-center text-sm text-neutral-500'>
              {isDisable ? `Disabling ${copy.workflows}…` : `Enabling ${copy.workflows}…`}
            </p>
          )}

          {state === 'success' && result != null && (
            <div className='space-y-3'>
              <div className='rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800'>
                <p>
                  {result.action === 'disable' ? 'Disabled' : 'Enabled'} {pipelineCopy(result.pipeline).workflows}.
                </p>
                <ul className='mt-2 list-inside list-disc break-all font-mono text-xs'>
                  {result.workflows.map((workflow) => (
                    <li key={workflow.workflowFile}>
                      {workflow.workflowFile} · {workflow.state}
                    </li>
                  ))}
                </ul>
              </div>
              {result.slackWarning != null && (
                <div className='rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950'>
                  Workflows updated, but Slack notify did not: {result.slackWarning}
                </div>
              )}
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          {state === 'confirm' && (
            <>
              <button
                type='button'
                onClick={onClose}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={() => void handleConfirm()}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                  isDangerous ? 'bg-red-600 hover:bg-red-700' : 'bg-neutral-900 hover:bg-neutral-800'
                }`}
              >
                {isDisable ? copy.confirmDisable : 'Confirm enable'}
              </button>
            </>
          )}
          {state === 'success' && (
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
